import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { isKioskDevice } from 'lib/helpers/kiosk';

const KIOSK_TOUCH_CLASS = 'kiosk-touch';
const TOUCH_UX_CLASS = 'touch-ux';
const COARSE_POINTER_QUERY = '(pointer: coarse)';

const PATIENT_TOUCH_PREFIXES = [
  '/mobile',
  '/checkin',
  '/instant-packet',
  '/ia',
  '/invoice',
  '/book',
  '/myportal',
  '/login'
];

// Captured the first time kiosk mode applies, so a device that stops being a kiosk (mobile
// kiosk, or the flag cleared on logout) gets its own viewport back instead of a guess.
let originalViewportContent = null;

const viewportMeta = () =>
  typeof document === 'undefined' ? null : document.querySelector('meta[name="viewport"]');

// Kiosk mode only differs from the app's own viewport by dropping maximum-scale (patients may
// need to zoom the glass) and keeping the on-screen keyboard resizing the layout.
const toKioskViewport = (content) => {
  const tokens = content
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token && !token.startsWith('maximum-scale'));

  if (!tokens.some((token) => token.startsWith('interactive-widget'))) {
    tokens.push('interactive-widget=resizes-content');
  }

  return tokens.join(', ');
};

const coarsePointerQuery = () =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? null
    : window.matchMedia(COARSE_POINTER_QUERY);

const isPatientTouchRoute = (pathname) =>
  PATIENT_TOUCH_PREFIXES.some((prefix) => pathname?.startsWith(prefix)) &&
  !!coarsePointerQuery()?.matches;

const applyTouchViewport = (meta) => {
  if (!meta) return;

  if (originalViewportContent === null) {
    originalViewportContent = meta.getAttribute('content') || '';
  }
  meta.setAttribute('content', toKioskViewport(originalViewportContent));
};

export const applyKioskTouchMode = (
  pathname = typeof window === 'undefined' ? '' : window.location.pathname
) => {
  if (typeof document === 'undefined') return false;

  const meta = viewportMeta();
  const { classList } = document.documentElement;

  if (isKioskDevice()) {
    classList.add(KIOSK_TOUCH_CLASS);
    classList.remove(TOUCH_UX_CLASS);
    applyTouchViewport(meta);
    return true;
  }

  if (isPatientTouchRoute(pathname)) {
    classList.remove(KIOSK_TOUCH_CLASS);
    classList.add(TOUCH_UX_CLASS);
    applyTouchViewport(meta);
    return true;
  }

  classList.remove(KIOSK_TOUCH_CLASS, TOUCH_UX_CLASS);
  if (originalViewportContent !== null) meta?.setAttribute('content', originalViewportContent);

  return false;
};

export const isKioskTouchMode = () =>
  typeof document !== 'undefined' &&
  (document.documentElement.classList.contains(KIOSK_TOUCH_CLASS) ||
    document.documentElement.classList.contains(TOUCH_UX_CLASS));

const useKioskTouchMode = () => {
  const { pathname } = useLocation();

  // Layout effect so the class lands before first paint, re-run per navigation because
  // KioskContextProvider writes the kiosk flag in an effect that runs after App has
  // rendered (and clears it on a mobile kiosk).
  useLayoutEffect(() => {
    applyKioskTouchMode(pathname);

    const query = coarsePointerQuery();
    if (!query?.addEventListener) return undefined;

    const handlePointerChange = () => applyKioskTouchMode(pathname);
    query.addEventListener('change', handlePointerChange);

    return () => query.removeEventListener('change', handlePointerChange);
  }, [pathname]);
};

export default useKioskTouchMode;
